import React from "react";
import { format } from "d3-format";
import {
  TooltipBodyRow,
  TooltipBodyRowLabel,
  TooltipBodyRowValue,
  SectorLabelWithColorIcon,
  ProductCodeLabel,
} from "@atlas-int/viz/shared";
import { ProductClass, ProductLevel, TradeDirection, TradeFlow } from "@atlas-int/data/types";
import { formatTotalValue, computeMonetaryValueByTradeFlow, getLocationQualifiers, UIView } from "@atlas-int/data";
import { getColorMap } from "@atlas-int/viz";
import { OverTimeLayoutOption } from "@atlas-int/viz/overtime";

import type {
  TooltipProductDatum,
  ProductMetadataByLevel,
} from "@atlas-int/viz/shared";

interface OverTimeTooltipContentProps {
  datum: TooltipProductDatum;
  view: UIView;
  tradeDirection: TradeDirection;
  tradeFlow: TradeFlow;
  overTimeLayout: string;
  exporter: string;
  importer: string;
  productClass?: ProductClass;
  productMetadata?: ProductMetadataByLevel;
}

/**
 * Tooltip content for Over Time chart
 */
export const OverTimeTooltipContent: React.FC<OverTimeTooltipContentProps> = ({
  datum,
  view,
  tradeDirection,
  tradeFlow,
  overTimeLayout,
  exporter,
  importer,
  productClass,
  productMetadata,
}) => {
  const { exporterIsWorld, importerIsWorld } = getLocationQualifiers({
    exporter,
    importer,
  });

  let netGrossLabelModifier;
  if (tradeFlow === TradeFlow.Gross) {
    netGrossLabelModifier = "Gross";
  } else if (tradeFlow === TradeFlow.Net) {
    netGrossLabelModifier = "Net";
  }

  if (view === UIView.Markets) {
    const { exportValue, importValue, year, shareForYear } = datum;

    let valueToUse;
    let labelToUse;
    let valueFormatterToUse;

    if (overTimeLayout === OverTimeLayoutOption.Share) {
      if (shareForYear !== undefined) {
        valueToUse = shareForYear;
        labelToUse = "Share";
        valueFormatterToUse = format(".2%");
      } else {
        valueToUse = undefined;
        labelToUse = undefined;
        valueFormatterToUse = undefined;
      }
    } else {
      if (tradeDirection === TradeDirection.Exports) {
        valueToUse = exportValue;
        labelToUse = netGrossLabelModifier
          ? `${netGrossLabelModifier} Export Value`
          : "Export Value";
      } else if (tradeDirection === TradeDirection.Imports) {
        valueToUse = importValue;
        labelToUse = netGrossLabelModifier
          ? `${netGrossLabelModifier} Import Value`
          : "Import Value";
      }
      valueFormatterToUse = (value: number) => "$" + formatTotalValue(value);
    }

    return (
      <>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>{labelToUse}:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>
            {valueFormatterToUse && valueFormatterToUse(valueToUse)}
          </TooltipBodyRowValue>
        </TooltipBodyRow>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>Year:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>{year}</TooltipBodyRowValue>
        </TooltipBodyRow>
      </>
    );
  } else if (view === UIView.Products && productMetadata !== undefined) {
    const {
      topLevelParent,
      shareForYear,
      year,
    } = datum;

    let colorMap = getColorMap({ view, productClass });
    let fillColor = colorMap ? colorMap.get(topLevelParent) : undefined;
    let matchingSector = productMetadata
      ? productMetadata.section.find(
          (productSection) => productSection.productId === topLevelParent,
        )
      : undefined;

    let valueToUse;
    let labelToUse;
    let valueFormatterToUse;

    if (overTimeLayout === OverTimeLayoutOption.Share) {
      if (shareForYear !== undefined) {
        valueToUse = shareForYear;
        labelToUse = "Share";
        valueFormatterToUse = format(".2%");
      } else {
        valueToUse = undefined;
        labelToUse = undefined;
        valueFormatterToUse = undefined;
      }
    } else {
      if (tradeDirection === TradeDirection.Exports) {
        valueToUse = computeMonetaryValueByTradeFlow({
          datum,
          tradeFlow: tradeFlow,
          tradeDirection,
        });
        labelToUse = netGrossLabelModifier
          ? `${netGrossLabelModifier} Export Value`
          : "Export Value";
      } else if (tradeDirection === TradeDirection.Imports) {
        valueToUse = computeMonetaryValueByTradeFlow({
          datum,
          tradeFlow: tradeFlow,
          tradeDirection,
        });
        labelToUse = netGrossLabelModifier
          ? `${netGrossLabelModifier} Import Value`
          : "Import Value";
      }
      valueFormatterToUse = (value: number) => "$" + formatTotalValue(value);
    }

    return (
      <>
        {matchingSector && (
          <TooltipBodyRow>
            <TooltipBodyRowLabel>Sector:</TooltipBodyRowLabel>
            <TooltipBodyRowValue>
              <SectorLabelWithColorIcon $fillColor={fillColor}>
                {matchingSector && matchingSector.nameShortEn}
              </SectorLabelWithColorIcon>
            </TooltipBodyRowValue>
          </TooltipBodyRow>
        )}
        <TooltipBodyRow>
          <TooltipBodyRowLabel>{labelToUse}:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>
            {valueFormatterToUse && valueFormatterToUse(valueToUse)}
          </TooltipBodyRowValue>
        </TooltipBodyRow>
        <TooltipBodyRow>
          <TooltipBodyRowLabel>Year:</TooltipBodyRowLabel>
          <TooltipBodyRowValue>{year}</TooltipBodyRowValue>
        </TooltipBodyRow>
      </>
    );
  }

  return null;
};

/**
 * Gets the tooltip header for Over Time chart
 */
export const getOverTimeTooltipHeader = (
  datum: TooltipProductDatum,
  view: UIView,
  productClass?: ProductClass,
  productMetadata?: ProductMetadataByLevel,
): React.ReactNode => {
  if (view === UIView.Markets) {
    const { groupName, nameEn } = datum;
    if (groupName) {
      return groupName;
    } else {
      return nameEn;
    }
  } else if (view === UIView.Products) {
    const { productId, productLevel, nameEn } = datum;

    let matchingLevelProductMetadata: ProductMetadataByLevel[keyof ProductMetadataByLevel] | undefined;
    if (productLevel == ProductLevel.ProductSection) {
      matchingLevelProductMetadata = productMetadata && productMetadata.section;
    } else if (productLevel == ProductLevel.Product2digit) {
      matchingLevelProductMetadata =
        productMetadata && productMetadata.twoDigit;
    } else if (productLevel == ProductLevel.Product4digit) {
      matchingLevelProductMetadata =
        productMetadata && productMetadata.fourDigit;
    } else if (productLevel == ProductLevel.Product6digit) {
      matchingLevelProductMetadata =
        productMetadata && productMetadata.sixDigit;
    }

    let matchingProductDatum = matchingLevelProductMetadata
      ? matchingLevelProductMetadata.find(
          (productDatum) => productDatum.productId === productId,
        )
      : undefined;

    let productCodeLabelValue =
      matchingProductDatum && `${matchingProductDatum.code} ${productClass}`;

    return (
      <div>
        {nameEn} | <ProductCodeLabel>{productCodeLabelValue}</ProductCodeLabel>
      </div>
    );
  }

  return null;
};
